/** Application-wide constants */

export const APP_NAME = 'WorkLynx';
export const DEFAULT_CURRENCY = 'AUD';
export const DEFAULT_LOCALE = 'en-AU';

/** localStorage keys */
export const STORAGE_KEYS = {
  THEME: 'wl_theme',
  ACTIVE_ORG_ID: 'wl_active_org_id',
} as const;

/** Subscription plan definitions */
export const SUBSCRIPTION_PLANS = [
  {
    id: 'tier_1',
    name: 'Starter',
    priceYearly: 240,
    monthlyEquivalent: 20,
    features: ['Up to 10 employees', 'Basic shift management', 'Timesheet tracking', 'Email support'],
  },
  {
    id: 'tier_2',
    name: 'Growth',
    priceYearly: 588,
    monthlyEquivalent: 49,
    features: [
      'Up to 30 employees',
      'Advanced shift management',
      'Recurring shifts',
      'Reports & analytics',
      'Priority support',
    ],
  },
  {
    id: 'tier_3',
    name: 'Business',
    priceYearly: 1188,
    monthlyEquivalent: 99,
    features: [
      'Up to 80 employees',
      'Full feature access',
      'Custom branding',
      'Dedicated support',
    ],
  },
  {
    id: 'tier_4_custom',
    name: 'Enterprise',
    priceYearly: -1,
    monthlyEquivalent: -1,
    features: [
      'Everything in Business',
      'Custom integrations',
      'SLA guarantee',
      'Dedicated account manager',
      'On-premise option',
    ],
  },
] as const;

/** Employment type labels */
export const EMPLOYMENT_TYPE_LABELS: Record<string, string> = {
  CASUAL: 'Casual',
  PART_TIME: 'Part-Time',
  FULL_TIME: 'Full-Time',
  ABN_CONTRACTOR: 'ABN Contractor',
  ACN_CONTRACTOR: 'ACN Contractor',
  INTERNSHIP: 'Internship',
  APPRENTICESHIP: 'Apprenticeship',
};

/** Shift status labels and colours */
export const SHIFT_STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  DRAFT: { label: 'Draft', color: 'text-muted' },
  PUBLISHED: { label: 'Published', color: 'text-info' },
  IN_PROGRESS: { label: 'In Progress', color: 'text-warning' },
  COMPLETED: { label: 'Completed', color: 'text-success' },
  CANCELLED: { label: 'Cancelled', color: 'text-danger' },
  DELETED: { label: 'Deleted', color: 'text-danger' },
};

/** Timesheet status labels and colours */
export const TIMESHEET_STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  OPEN: { label: 'Open', color: 'text-muted' },
  SUBMITTED: { label: 'Submitted', color: 'text-info' },
  APPROVED: { label: 'Approved', color: 'text-success' },
  REJECTED: { label: 'Rejected', color: 'text-danger' },
  LOCKED: { label: 'Locked', color: 'text-warning' },
};

/** Recurring cadence labels */
export const CADENCE_LABELS: Record<string, string> = {
  DAILY: 'Daily',
  WEEKLY: 'Weekly',
  FORTNIGHTLY: 'Fortnightly',
  MONTHLY: 'Monthly',
  CUSTOM: 'Custom',
};

/** Billing type labels */
export const BILLING_TYPE_LABELS: Record<string, string> = {
  HOURLY: 'Per Hour',
  PER_SHIFT: 'Per Shift',
  WEEKLY_FLAT: 'Weekly Flat Rate',
  FORTNIGHTLY_FLAT: 'Fortnightly Flat Rate',
  MONTHLY_FLAT: 'Monthly Flat Rate',
};

/** Invoice frequency labels */
export const INVOICE_FREQUENCY_LABELS: Record<string, string> = {
  WEEKLY: 'Weekly',
  FORTNIGHTLY: 'Fortnightly',
  MONTHLY: 'Monthly',
  QUARTERLY: 'Quarterly (3 months)',
  BIANNUALLY: 'Biannually (6 months)',
  YEARLY: 'Yearly',
};

/** Job status labels and colours */
export const JOB_STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  ACTIVE: { label: 'Active', color: 'text-success' },
  PAUSED: { label: 'Paused', color: 'text-warning' },
  COMPLETED: { label: 'Completed', color: 'text-muted' },
};

/** Trial duration in days */
export const TRIAL_DURATION_DAYS = 30;
